import { Injectable } from '@angular/core';

/**
 * Log level enumeration for categorizing log messages
 */
export enum LogLevel {
  LOG = 'LOG',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

/**
 * Interface for buffered log entries
 */
export interface LogEntry {
  level: LogLevel;
  args: unknown[];
  timestamp: number;
}

/**
 * LoggingService provides a unified interface for application logging.
 *
 * Features:
 * - Standard logging methods: log(), debug(), warn(), error()
 * - Buffering mode for testing: enables queryable log storage
 * - Injectable and mockable for testability
 * - By default, logs are sent to the console
 *
 * Usage in components/services:
 * ```typescript
 * constructor(private logger: LoggingService) {}
 *
 * someMethod() {
 *   this.logger.log('Operation started');
 *   this.logger.warn('Deprecated feature used');
 *   this.logger.error('Operation failed', error);
 * }
 * ```
 *
 * Usage in tests:
 * ```typescript
 * beforeEach(() => {
 *   logger.enableBuffering();
 *   logger.clearBuffer();
 * });
 *
 * it('should log error message', () => {
 *   component.performAction();
 *   const errorLogs = logger.getBufferedLogs(LogLevel.ERROR);
 *   expect(errorLogs).toHaveLength(1);
 *   expect(errorLogs[0].args[0]).toBe('Expected error message');
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private bufferingEnabled = false;
  private logBuffer: LogEntry[] = [];

  /**
   * Log a message (synonym: debug)
   * @param args - Any number of arguments to log
   */
  log(...args: unknown[]): void {
    this.writeLog(LogLevel.LOG, args);
  }

  /**
   * Log a debug message (synonym for log)
   * @param args - Any number of arguments to log
   */
  debug(...args: unknown[]): void {
    this.writeLog(LogLevel.LOG, args);
  }

  /**
   * Log a warning message
   * @param args - Any number of arguments to log
   */
  warn(...args: unknown[]): void {
    this.writeLog(LogLevel.WARN, args);
  }

  /**
   * Log an error message
   * @param args - Any number of arguments to log
   */
  error(...args: unknown[]): void {
    this.writeLog(LogLevel.ERROR, args);
  }

  /**
   * Enable buffering mode for tests.
   * When enabled, logs are stored in memory instead of being sent to console.
   */
  enableBuffering(): void {
    this.bufferingEnabled = true;
  }

  /**
   * Disable buffering mode.
   * Logs will resume being sent to console.
   */
  disableBuffering(): void {
    this.bufferingEnabled = false;
  }

  /**
   * Clear the log buffer
   */
  clearBuffer(): void {
    this.logBuffer = [];
  }

  /**
   * Get buffered logs, optionally filtered by log level
   * @param level - Optional log level to filter by
   * @returns Array of log entries
   */
  getBufferedLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logBuffer.filter((entry) => entry.level === level);
    }
    return [...this.logBuffer];
  }

  /**
   * Internal method to write logs
   * @param level - Log level
   * @param args - Arguments to log
   */
  private writeLog(level: LogLevel, args: unknown[]): void {
    if (this.bufferingEnabled) {
      this.logBuffer.push({
        level,
        args,
        timestamp: Date.now(),
      });
    } else {
      this.writeToConsole(level, args);
    }
  }

  /**
   * Write to console based on log level
   * @param level - Log level
   * @param args - Arguments to log
   */
  private writeToConsole(level: LogLevel, args: unknown[]): void {
    switch (level) {
      case LogLevel.LOG:
        console.log(...args);
        break;
      case LogLevel.WARN:
        console.warn(...args);
        break;
      case LogLevel.ERROR:
        console.error(...args);
        break;
    }
  }
}
